import { ChangeDetectionStrategy, Component, Inject } from '@angular/core';
import { TranslateModule } from '@ngx-translate/core';
import { MatProgressSpinnerModule, ProgressSpinnerMode } from '@angular/material/progress-spinner';
import { MAT_SNACK_BAR_DATA, MatSnackBarModule, MatSnackBarRef } from '@angular/material/snack-bar';
import { CustomLinkDirective } from '@shared/directives/custom-link/custom-link.directive';
import { BehaviorSubject, filter, finalize, Observable, tap } from 'rxjs';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { HttpEvent, HttpEventType, HttpResponse } from '@angular/common/http';
import { AsyncPipe } from '@angular/common';
import { map } from 'rxjs/operators';
import { filterNil } from 'ngxtension/filter-nil';

@Component({
  selector: 'ac-download-indicator',
  standalone: true,
  imports: [TranslateModule, MatProgressSpinnerModule, MatSnackBarModule, CustomLinkDirective, AsyncPipe],
  templateUrl: './download-indicator.component.html',
  styleUrls: ['./download-indicator.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class DownloadIndicatorComponent {
  protected _mode = new BehaviorSubject<ProgressSpinnerMode>('indeterminate');
  protected _progress = new BehaviorSubject<number>(0);
  constructor(
    private _snackBarRef: MatSnackBarRef<DownloadIndicatorComponent>,
    @Inject(MAT_SNACK_BAR_DATA) private _data: { obs: Observable<HttpEvent<Blob>>; filename: string }
  ) {
    _data.obs
      .pipe(
        finalize(() => _snackBarRef.dismiss()),
        tap((event) => this._trackProgress(event)),
        filter((value): value is HttpResponse<Blob> => value.type === HttpEventType.Response),
        map((value) => value.body),
        filterNil(),
        takeUntilDestroyed()
      )
      .subscribe((response) => {
        const dataType = response.type;
        const isExcel = dataType === 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet';
        const binaryData = [];
        binaryData.push(response);
        const downloadLink = document.createElement('a');
        downloadLink.target = '_blank';
        downloadLink.href = window.URL.createObjectURL(new Blob(binaryData, { type: dataType }));
        if (_data.filename) {
          downloadLink.setAttribute('download', `${_data.filename}${isExcel ? '.xlsx' : ''}`);
        }
        document.body.appendChild(downloadLink);
        downloadLink.click();
      });
  }
  dismiss(): void {
    this._snackBarRef.dismiss();
  }

  private _trackProgress(event: HttpEvent<Blob>): void {
    switch (event.type) {
      case HttpEventType.Sent:
      case HttpEventType.ResponseHeader:
      case HttpEventType.UploadProgress:
      case HttpEventType.User:
        this._mode.next('indeterminate');
        break;
      case HttpEventType.Response:
        this._progress.next(100);
        this._mode.next('determinate');
        break;
      case HttpEventType.DownloadProgress:
        if (event.total) {
          this._progress.next((event.loaded * 100) / event.total);
          this._mode.next('determinate');
        } else {
          this._mode.next('indeterminate');
        }
        break;
    }
  }
}
